import React, { useState, useEffect, useCallback, useRef } from 'react';

const TOTP_INTERVAL_SECONDS = 30;

function getCsrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.getAttribute('content') : '';
}

function getTimeRemaining() {
  const epochSeconds = Math.floor(Date.now() / 1000);
  const elapsedSeconds = epochSeconds % TOTP_INTERVAL_SECONDS;

  return elapsedSeconds === 0 ? TOTP_INTERVAL_SECONDS : TOTP_INTERVAL_SECONDS - elapsedSeconds;
}

function TwoFactorChallenge({ verifyUrl, backupUrl, loginUrl, translations }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(getTimeRemaining);
  const inputRef = useRef(null);

  useEffect(() => {
    const syncTimer = () => {
      setTimeRemaining(getTimeRemaining());
    };

    syncTimer();
    const interval = setInterval(syncTimer, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  const handleCodeChange = useCallback((e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(value);
    setError(null);
  }, []);

  const handleSubmit = useCallback((e) => {
    if (e) e.preventDefault();
    if (code.length !== 6 || submitting) return;

    setSubmitting(true);
    setError(null);

    fetch(verifyUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': getCsrfToken(),
        Accept: 'application/json'
      },
      body: JSON.stringify({ code: code })
    })
      .then((res) => {
        if (res.status === 401) {
          window.location.href = loginUrl;
          return null;
        }
        return res.json();
      })
      .then((data) => {
        if (!data) return;

        if (data.success) {
          const redirectUrl = data.redirect_url || '/';

          if (window.Turbolinks) {
            Turbolinks.visit(redirectUrl);
          } else {
            window.location.href = redirectUrl;
          }
        } else {
          setError(data.message || translations.invalid_code);
          setCode('');
          setSubmitting(false);

          if (inputRef.current) inputRef.current.focus();
        }
      })
      .catch(() => {
        setError(translations.invalid_code);
        setCode('');
        setSubmitting(false);
      });
  }, [code, submitting, verifyUrl, loginUrl, translations]);

  useEffect(() => {
    if (code.length === 6) {
      handleSubmit();
    }
  }, [code, handleSubmit]);

  const progress = (timeRemaining / TOTP_INTERVAL_SECONDS) * 100;
  const radius = 58;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (progress / 100) * circumference;
  const timerValue = String(timeRemaining).padStart(2, '0');
  const timerLabel = translations.time_remaining.replace('__seconds__', timerValue);
  let timerClassName = 'two-factor-challenge__timer';

  if (timeRemaining <= 5) {
    timerClassName += ' two-factor-challenge__timer--danger';
  } else if (timeRemaining <= 10) {
    timerClassName += ' two-factor-challenge__timer--warning';
  }

  return (
    <div className="two-factor-challenge">
      <a href={loginUrl} className="two-factor-challenge__back login-back-link">
        <i className="zmdi zmdi-arrow-left" />
        {translations.back_to_login}
      </a>

      <div className="two-factor-challenge__hero">
        <div className="two-factor-challenge__shield">
          <i className="zmdi zmdi-shield-security" />
        </div>

        <div className="two-factor-challenge__hero-copy">
          <h2 className="two-factor-challenge__title">{translations.title}</h2>
          <p className="two-factor-challenge__desc">{translations.description}</p>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2 mb-3">{error}</div>}

      <form onSubmit={handleSubmit}>
        <label htmlFor="two-factor-challenge-code" className="two-factor-challenge__label">
          {translations.label}
        </label>

        <div className="two-factor-challenge__input-group">
          <div className="form-group">
            <input
              ref={inputRef}
              id="two-factor-challenge-code"
              type="text"
              className="form-control two-factor-code-input two-factor-code-input--large"
              value={code}
              onChange={handleCodeChange}
              placeholder={translations.placeholder}
              maxLength={6}
              autoComplete="one-time-code"
              inputMode="numeric"
              disabled={submitting}
            />
            <i className="form-group__bar" />
          </div>

          <div className="two-factor-challenge__timer-card">
            <div className={timerClassName}>
              <svg
                className="two-factor-challenge__timer-ring"
                width="148"
                height="148"
                viewBox="0 0 148 148"
                aria-hidden="true"
              >
                <circle className="two-factor-challenge__timer-track" cx="74" cy="74" r={radius} />
                <circle
                  className="two-factor-challenge__timer-progress"
                  cx="74"
                  cy="74"
                  r={radius}
                  strokeDasharray={circumference}
                  strokeDashoffset={dashOffset}
                  style={{ transition: 'stroke-dashoffset 1s linear' }}
                />
              </svg>

              <div className="two-factor-challenge__timer-value">
                {timerValue}
              </div>
            </div>

            <div className="two-factor-challenge__timer-copy">
              <div className="two-factor-challenge__timer-title">{timerLabel}</div>
            </div>

            <div className="two-factor-challenge__timer-badge">
              <i className="zmdi zmdi-shield-security" />
              <span>TOTP</span>
            </div>
          </div>
        </div>

        <button
          type="submit"
          className="btn btn-primary login-btn w-100 mt-4"
          disabled={submitting || code.length !== 6}
        >
          {submitting ? translations.verifying : translations.verify_button}
          {!submitting && <span className="login-btn__arrow">&rarr;</span>}
        </button>
      </form>

      <div className="two-factor-challenge__links mt-4">
        <a href={backupUrl} className="two-factor-challenge__backup-link">
          <i className="zmdi zmdi-key" />
          {translations.use_backup}
        </a>
      </div>
    </div>
  );
}

export default TwoFactorChallenge;
